/**
 * TourContext — global tour orchestration.
 *
 * The Guided Tours feature overlays a *narrator panel* on top of the real
 * application screens and silently switches authentication persona between
 * scenes. This way visitors see the **actual product**, not bespoke mocks.
 *
 * Lifecycle
 *   startTour(tour)       — begin a tour from scene 0
 *   advance() / back()    — manual scene navigation
 *   pause()/resume()      — auto-advance control
 *   endTour()             — log out, clear context, /tours
 *
 * On every scene change the provider:
 *   1. Awaits a persona login (calls authApi.* with hardcoded demo creds)
 *   2. Navigates to scene.route
 *   3. Schedules the next auto-advance (scene.autoAdvanceMs)
 *
 * URL also carries `?tour=<id>&scene=<n>` so the floating panel survives
 * route navigations and supports deep-linking / replay.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from './AppContext';
import { findTour, nextTourInCollection } from '../data/tours/registry';
import { useTourVoice } from '../components/tours/useTourVoice';
import { adminApi } from '../api/client';
import { toast } from 'sonner';

const TourContext = createContext(null);

const DEFAULT_SCENE_MS = 8000;
const TICK_MS = 100;

// Demo credentials per persona role. These come from /app/memory/test_credentials.md
// and are used to silently auto-login when a tour scene switches personas.
const PERSONA_CREDENTIALS = {
  'Neighbor':         { kind: 'neighbor', phone: '317-555-1001', otp: '123456' }, // Maria Rodriguez (real demo neighbor)
  'Case Manager':     { kind: 'staff',    email: 'cm@jbnc.org',  password: 'demo' },
  'Career Coach':     { kind: 'staff',    email: 'cc@jbnc.org',  password: 'demo' },
  'Program Manager':  { kind: 'staff',    email: 'pm@jbnc.org',  password: 'demo' },
  'Executive':        { kind: 'staff',    email: 'exec@jbnc.org',password: 'demo' },
  'Grants Manager':   { kind: 'staff',    email: 'grants@jbnc.org', password: 'demo' },
  'Youth Worker':     { kind: 'staff',    email: 'yw@jbnc.org',  password: 'demo' },
  'Community Partner':{ kind: 'staff',    email: 'partner@eastside.org', password: 'demo' },
  'Super Admin':      { kind: 'admin',    email: 'admin@district.app', password: 'demo' },
  // 'Community Member' has NO credentials — the tour engine treats this as
  // an explicit logout so the CommunityEvents page renders as truly public
  // (no sidebar, no logged-in Neighbor identity). See loginAsPersona below.
  'Community Member': { kind: 'public' },
};

const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function safeFetch(url, opts) {
  try { const r = await fetch(url, opts); if (!r.ok) return null; return await r.json(); }
  catch (_) { return null; }
}

// Yield a couple of frames so React's batched state updates from the auth
// context can flush BEFORE we navigate into a protected route. Without this,
// ProtectedRoute reads the old isAuthenticated=false and redirects to login.
const flushReactState = () => new Promise(resolve => {
  setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(resolve)), 0);
});

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { staffLogin, adminLogin, neighborLogin, logout, userRole } = useApp();

  const [tour, setTour]               = useState(null);
  const [sceneIndex, setSceneIndex]   = useState(0);
  const [beatIndex, setBeatIndex]     = useState(0); // active beat within scene.beats (only used when scene declares beats)
  const [isPlaying, setIsPlaying]     = useState(true);
  const [elapsedMs, setElapsedMs]     = useState(0);
  const [runId, setRunId]             = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  const tickRef = useRef();
  const sceneSeqRef = useRef(0); // guards against stale persona-switch resolutions
  const interactionTimersRef = useRef([]); // pending interaction setTimeouts to clear on scene change
  const advanceTimerRef = useRef(null); // post-narration 1.1 s advance timeout
  const fallbackAdvanceTimerRef = useRef(null); // long-timer safety net
  // Wall-clock timestamp at which startTour() was called. Powers the
  // "we just watched it happen in ⏱ 4:12" stopwatch badge that closing
  // Narrator scenes can opt into via `showElapsedBadge: true` — turns the
  // pitch from "it's fast" into an actual clock proof for funders.
  const [tourStartedAt, setTourStartedAt] = useState(null);
  // Refs that mirror state so async callbacks (voice.onEnd) read fresh values
  // without us needing to re-create them on every state change.
  const isPlayingRef = useRef(true);
  const sceneIndexRef = useRef(0);
  const totalScenesRef = useRef(0);

  // Simulated cursor state — driven by the per-scene `interactions` runner.
  // The TourCursor component reads this and renders an animated arrow at (x, y).
  const [cursorState, setCursorState] = useState({ visible: false, x: 0, y: 0, clicking: false, label: '' });

  const voice = useTourVoice();

  // Login that goes through AppContext so React state stays in sync with localStorage
  const loginAsPersona = useCallback(async (role) => {
    if (!role || role === 'Narrator' || role === 'System') return true;
    // Already signed in as the right role? Skip.
    if (userRole === role) return true;
    const cred = PERSONA_CREDENTIALS[role];
    if (!cred) return false;
    try {
      // 'Community Member' persona is an EXPLICIT log-out — the tour renders
      // truly public surfaces (no sidebar, no authenticated identity leak)
      // for the "before they had an account" chapters of the story.
      if (cred.kind === 'public') { logout(); return true; }
      let res;
      if (cred.kind === 'neighbor')      res = await neighborLogin(cred.phone, cred.otp);
      else if (cred.kind === 'admin')    res = await adminLogin(cred.email, cred.password);
      else                                res = await staffLogin(cred.email, cred.password);
      return !!res?.success;
    } catch (e) { console.warn('[tour] persona login failed', role, e); return false; }
  }, [userRole, staffLogin, adminLogin, neighborLogin, logout]);

  const totalScenes = tour?.scenes?.length || 0;
  const currentScene = tour?.scenes?.[sceneIndex] || null;
  const sceneMs = currentScene?.autoAdvanceMs || DEFAULT_SCENE_MS;

  // ── Start / end ──────────────────────────────────────────────────────
  const startTour = useCallback(async (tourId) => {
    const meta = findTour(tourId);
    if (!meta || meta.status !== 'live') {
      navigate('/tours');
      return;
    }
    // Silently reset the writable demo state so every tour run starts from
    // a known good baseline: Demo Pending neighbor in the PM approval queue,
    // capacity-worthy referrals seeded for the partner-capacity alerts,
    // etc. Without this, a previous tour run that clicked "Approve" would
    // leave the queue empty and break the narration on the next replay.
    // Best-effort — if the reset fails we still try to start the tour.
    try { await adminApi.demoReset(); } catch (_) { /* ignore */ }

    // Reset the shared tour-context bag so IDs written by a previous tour
    // run don't leak into this one (e.g. yesterday's event id would make
    // Scene 4-5-6 spotlight the wrong row).
    if (typeof window !== 'undefined') window.__tourCtx = {};

    // Force CM Classic layout for the duration of the tour. The tours are
    // authored against the Classic CM UI; if the current user previously
    // toggled into Focus Mode that preference is localStorage-persisted and
    // would override every CM-role scene's expected layout.
    try {
      localStorage.setItem('district_cm_focus', 'false');
      window.dispatchEvent(new Event('district:force-classic-mode'));
    } catch (_) { /* ignore */ }

    const mod = await meta.loader();
    const fullTour = { ...meta, ...(mod.default || mod) };
    setTour(fullTour);
    setSceneIndex(0);
    setElapsedMs(0);
    setIsPlaying(true);
    setTourStartedAt(Date.now());

    // Log a tour-run start (best-effort)
    const res = await safeFetch(`${apiBase}/tours/runs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tourId: fullTour.id, totalScenes: fullTour.scenes.length }),
    });
    if (res?.runId) setRunId(res.runId);
  }, [navigate]);

  const endTour = useCallback(async () => {
    if (runId) {
      safeFetch(`${apiBase}/tours/runs/${runId}/end`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: sceneIndex >= totalScenes - 1 }),
      });
    }
    voice.cancel();
    // Inline cleanup (avoid TDZ — clearInteractionTimers is declared below)
    interactionTimersRef.current.forEach(timer => clearTimeout(timer));
    interactionTimersRef.current = [];
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    if (fallbackAdvanceTimerRef.current) { clearTimeout(fallbackAdvanceTimerRef.current); fallbackAdvanceTimerRef.current = null; }
    setCursorState({ visible: false, x: 0, y: 0, clicking: false, label: '' });
    // Clear tour state and navigate to the public tours index. We
    // deliberately do NOT call logout() here — exiting a tour mid-protected
    // route would trigger ProtectedRoute to redirect to /app/login before
    // our navigate('/tours') is committed. The visitor never explicitly
    // signed in, so leaving the persona token in localStorage is harmless
    // and the next tour will overwrite it with the right persona.
    setTour(null);
    setSceneIndex(0);
    setRunId(null);
    setIsPlaying(false);
    setTourStartedAt(null);
    // Auto-chain: if this tour was launched with ?chain=1 (from the "Play
    // from the top" CTA on a storyline poster) and it belongs to a
    // collection with a next leg, jump straight into the next leg without
    // dumping the viewer back at /tours. Runs the whole arc hands-free.
    const chainParam = new URLSearchParams(location.search).get('chain');
    if (chainParam === '1' && tour?.id) {
      const next = nextTourInCollection(tour.id);
      if (next) {
        navigate(`/tours/run/${next.id}?chain=1`);
        return;
      }
    }
    navigate('/tours');
  }, [runId, sceneIndex, totalScenes, navigate, voice, location.search, tour]);

  // ── Per-scene interaction runner ────────────────────────────────────
  // Each scene may declare:
  //   interactions: [
  //     { at: 2500, target: '[data-testid="..."]', action: 'point' | 'click', label?: 'click Approve', dispatch?: true }
  //   ]
  // The runner:
  //   1. clears any timers from the previous scene
  //   2. for each interaction, schedules a setTimeout that:
  //      - finds the target element by selector
  //      - smooth-glides the cursor to its center (CSS transition handles motion)
  //      - if action==='click', plays a ripple at the target and (if
  //        dispatch !== false) fires a real DOM click() on the element
  //      - if action==='point', leaves the cursor at the target with no ripple
  //   3. hides the cursor when the scene has no remaining interactions
  const clearInteractionTimers = useCallback(() => {
    interactionTimersRef.current.forEach(t => clearTimeout(t));
    interactionTimersRef.current = [];
  }, []);

  // Tour context bag — cross-scene shared state so scenes can thread real
  // ids (event ids, rsvp ids, neighbor ids) through the interactions. Any
  // component can dispatch `district:tour-ctx` with `{key, value}` to write.
  // Selectors and typed values in interactions can interpolate via `${ctx.foo}`.
  const ensureTourCtx = () => {
    if (typeof window !== 'undefined' && !window.__tourCtx) window.__tourCtx = {};
    return (typeof window !== 'undefined' ? window.__tourCtx : {}) || {};
  };
  const interpolateCtx = (str) => {
    if (typeof str !== 'string') return str;
    if (!str.includes('${ctx.') && !str.includes('{{ctx.')) return str;
    const ctx = ensureTourCtx();
    return str
      .replace(/\$\{ctx\.([a-zA-Z0-9_]+)\}/g, (_m, k) => (ctx[k] != null ? String(ctx[k]) : ''))
      .replace(/\{\{ctx\.([a-zA-Z0-9_]+)\}\}/g, (_m, k) => (ctx[k] != null ? String(ctx[k]) : ''));
  };
  React.useEffect(() => {
    const h = (e) => {
      const ctx = ensureTourCtx();
      const { key, value } = e?.detail || {};
      if (key) ctx[key] = value;
    };
    window.addEventListener('district:tour-ctx', h);
    return () => window.removeEventListener('district:tour-ctx', h);
  }, []);

  const runInteractions = useCallback((scene, seq) => {
    clearInteractionTimers();
    setCursorState(prev => ({ ...prev, visible: false, clicking: false, label: '' }));
    const list = Array.isArray(scene?.interactions) ? scene.interactions : [];
    if (list.length === 0) return;

    const performInteraction = (interactionRaw) => {
      if (seq !== sceneSeqRef.current) return;
      // Interpolate any `${ctx.xxx}` tokens against window.__tourCtx so
      // scenes can target dynamically-created DOM (event ids, rsvp ids, etc).
      const interaction = { ...interactionRaw };
      if (typeof interaction.target === 'string') interaction.target = interpolateCtx(interaction.target);
      if (Array.isArray(interaction.selectors)) interaction.selectors = interaction.selectors.map(interpolateCtx);
      if (typeof interaction.text === 'string')  interaction.text  = interpolateCtx(interaction.text);
      if (typeof interaction.value === 'string') interaction.value = interpolateCtx(interaction.value);
      // 'navigate' has a route string (not a CSS selector) so we short-
      // circuit before the element lookup below.
      if (interaction.action === 'navigate') {
        if (interaction.target) {
          try { navigate(interaction.target); } catch (_) { /* ignore */ }
        }
        return;
      }
      // 'dispatch' fires a global CustomEvent and has no DOM target — short-
      // circuit before the element lookup so a missing selector doesn't
      // silently skip the event fan-out (was the root cause of Scene 5's
      // indicator tick never landing on the RHT card).
      if (interaction.action === 'dispatch') {
        try {
          const evName = interaction.event || 'district:tour-event';
          window.dispatchEvent(new CustomEvent(evName, { detail: interaction.detail || {} }));
        } catch (_) { /* */ }
        return;
      }
      const el = document.querySelector(interaction.target)
        || (Array.isArray(interaction.selectors)
              ? interaction.selectors.map(s => document.querySelector(s)).find(Boolean)
              : null);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const x = rect.left + rect.width / 2;
      const y = rect.top  + rect.height / 2;
      // Glide cursor to the target (label visible during glide)
      setCursorState({ visible: true, x, y, clicking: false, label: interaction.label || '' });
      const action = interaction.action || 'point';
      if (action === 'click' || action === 'clickFirstMatch') {
        // After the glide completes (~1.1s), fire the ripple + DOM click.
        const t1 = setTimeout(() => {
          if (seq !== sceneSeqRef.current) return;
          setCursorState(prev => ({ ...prev, clicking: true }));
          if (interaction.dispatch !== false) {
            try { el.click(); } catch (_) { /* ignore */ }
          }
          // Drop the ripple flag a moment later
          const t2 = setTimeout(() => {
            if (seq !== sceneSeqRef.current) return;
            setCursorState(prev => ({ ...prev, clicking: false }));
          }, 750);
          interactionTimersRef.current.push(t2);
        }, 1150);
        interactionTimersRef.current.push(t1);
      } else if (action === 'type') {
        // After the glide completes, focus the field and animate typing the
        // string character-by-character. Uses React's internal value setter
        // so controlled `onChange` handlers receive the change.
        const text  = String(interaction.text ?? '');
        const speed = Math.max(30, interaction.speed || 90); // ms per char
        const t1 = setTimeout(() => {
          if (seq !== sceneSeqRef.current) return;
          try { el.focus(); } catch (_) { /* ignore */ }
          // React patches HTMLInputElement.value, so we need the *native*
          // setter to fool React into seeing the change.
          const proto = el instanceof HTMLTextAreaElement
            ? window.HTMLTextAreaElement.prototype
            : window.HTMLInputElement.prototype;
          const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
          const setVal = (v) => {
            if (setter) setter.call(el, v); else { try { el.value = v; } catch (_) { /* */ } }
            try { el.dispatchEvent(new Event('input',  { bubbles: true })); } catch (_) { /* */ }
            try { el.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) { /* */ }
          };
          let typed = '';
          const typeNext = () => {
            if (seq !== sceneSeqRef.current) return;
            if (typed.length >= text.length) {
              try { el.dispatchEvent(new Event('blur', { bubbles: true })); } catch (_) { /* */ }
              return;
            }
            typed += text[typed.length];
            setVal(typed);
            const t = setTimeout(typeNext, speed);
            interactionTimersRef.current.push(t);
          };
          typeNext();
        }, 1100);
        interactionTimersRef.current.push(t1);
      } else if (action === 'setValue') {
        // One-shot value set — for inputs that don't accept character-by-
        // character typing (e.g. `datetime-local`, `color`, `range`).
        // Same native-setter trick as `type` so React's onChange fires.
        const value = String(interaction.value ?? interaction.text ?? '');
        const t1 = setTimeout(() => {
          if (seq !== sceneSeqRef.current) return;
          try { el.focus(); } catch (_) { /* ignore */ }
          const proto = el instanceof HTMLTextAreaElement
            ? window.HTMLTextAreaElement.prototype
            : window.HTMLInputElement.prototype;
          const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
          if (setter) setter.call(el, value); else { try { el.value = value; } catch (_) { /* */ } }
          try { el.dispatchEvent(new Event('input',  { bubbles: true })); } catch (_) { /* */ }
          try { el.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) { /* */ }
          try { el.dispatchEvent(new Event('blur',   { bubbles: true })); } catch (_) { /* */ }
        }, 1100);
        interactionTimersRef.current.push(t1);
      } else if (action === 'select') {
        // Set a value on a native `<select>` (dropdown). Uses the same
        // native-value-setter trick as `type` so React's onChange fires.
        const value = String(interaction.value ?? '');
        const t1 = setTimeout(() => {
          if (seq !== sceneSeqRef.current) return;
          try { el.focus(); } catch (_) { /* ignore */ }
          const setter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')?.set;
          if (setter) setter.call(el, value); else { try { el.value = value; } catch (_) { /* */ } }
          try { el.dispatchEvent(new Event('input',  { bubbles: true })); } catch (_) { /* */ }
          try { el.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) { /* */ }
          setCursorState(prev => ({ ...prev, clicking: true }));
          const t2 = setTimeout(() => {
            if (seq !== sceneSeqRef.current) return;
            setCursorState(prev => ({ ...prev, clicking: false }));
          }, 600);
          interactionTimersRef.current.push(t2);
        }, 1100);
        interactionTimersRef.current.push(t1);
      } else if (action === 'intakeAutopilot') {
        // Field-aware intake answering: reads the dialog's
        // `data-current-field-id` attribute on every cycle, looks up the
        // matching answer in `interaction.answers`, types it, clicks Send,
        // waits for the dialog to advance to the next question, and repeats.
        // No more typing "Yes" into a First Name field.
        const answers = interaction.answers || {};
        const fallback = interaction.fallback || 'Yes';
        const maxCycles = Math.max(1, interaction.maxCycles || 4);
        const sendSelector = interaction.sendSelector || '[data-testid="intake-send-btn"]';
        const dialogSelector = interaction.dialogSelector || '[data-testid="intake-chat-dialog"]';
        const charSpeed = Math.max(30, interaction.speed || 70);

        // Resolve the answer for the current field. Try id first, then a
        // case-insensitive substring match on the field's human label
        // ("First Name" → matches key "first").
        const pickAnswer = () => {
          const dlg = document.querySelector(dialogSelector);
          if (!dlg) return null;
          const fieldId = dlg.getAttribute('data-current-field-id') || '';
          const label = (dlg.getAttribute('data-current-field-label') || '').toLowerCase();
          if (fieldId && answers[fieldId] != null) return { key: fieldId, value: String(answers[fieldId]) };
          for (const [k, v] of Object.entries(answers)) {
            if (k && label.includes(k.toLowerCase())) return { key: k, value: String(v) };
          }
          return { key: 'fallback', value: String(fallback) };
        };

        let cycle = 0;
        const runCycle = () => {
          if (seq !== sceneSeqRef.current) return;
          if (cycle >= maxCycles) return;
          const input = document.querySelector(interaction.target);
          if (!input) return; // dialog closed
          const pick = pickAnswer();
          if (!pick) return;
          const rect = input.getBoundingClientRect();
          if (rect.width === 0 && rect.height === 0) {
            // Field type doesn't have a typeable input (e.g. radio group);
            // skip via "Looks good" if available, otherwise abort.
            const lg = document.querySelector('[data-testid="intake-looks-good-btn"]');
            if (lg) { try { lg.click(); } catch (_) { /* */ } }
            cycle += 1;
            const t = setTimeout(runCycle, 2200);
            interactionTimersRef.current.push(t);
            return;
          }
          setCursorState({
            visible: true,
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
            clicking: false,
            label: `${pick.key}: ${pick.value}`.slice(0, 40),
          });
          const proto = input instanceof HTMLTextAreaElement
            ? window.HTMLTextAreaElement.prototype
            : window.HTMLInputElement.prototype;
          const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
          const setVal = (v) => {
            if (setter) setter.call(input, v); else { try { input.value = v; } catch (_) { /* */ } }
            try { input.dispatchEvent(new Event('input',  { bubbles: true })); } catch (_) { /* */ }
            try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) { /* */ }
          };
          // Pause briefly to "read" the question, then type the answer.
          const startType = setTimeout(() => {
            if (seq !== sceneSeqRef.current) return;
            try { input.focus(); } catch (_) { /* */ }
            let typed = '';
            const typeNext = () => {
              if (seq !== sceneSeqRef.current) return;
              if (typed.length >= pick.value.length) {
                // Click Send, advance to next cycle after a short wait
                const send = document.querySelector(sendSelector);
                if (send) {
                  const sr = send.getBoundingClientRect();
                  setCursorState({ visible: true, x: sr.left + sr.width / 2, y: sr.top + sr.height / 2, clicking: true, label: 'send \u2192' });
                  try { send.click(); } catch (_) { /* */ }
                  const drop = setTimeout(() => {
                    if (seq !== sceneSeqRef.current) return;
                    setCursorState(prev => ({ ...prev, clicking: false }));
                  }, 500);
                  interactionTimersRef.current.push(drop);
                }
                cycle += 1;
                const wait = setTimeout(runCycle, 2400);
                interactionTimersRef.current.push(wait);
                return;
              }
              typed += pick.value[typed.length];
              setVal(typed);
              const t = setTimeout(typeNext, charSpeed);
              interactionTimersRef.current.push(t);
            };
            typeNext();
          }, 800);
          interactionTimersRef.current.push(startType);
        };
        runCycle();
      } else if (action === 'enrollAll') {
        // Sweep every visible Enroll button in the intake dialog's enrollment
        // phase, click them in turn so viewers see each program flip to an
        // "Enrolled" badge with a confirmation toast. Works against the
        // existing `enroll-btn-{programId}` testids — no scene-author bookkeeping.
        const selector = interaction.target || '[data-testid^="enroll-btn-"]';
        const step = Math.max(400, interaction.step || 900);
        const start = setTimeout(() => {
          if (seq !== sceneSeqRef.current) return;
          const btns = Array.from(document.querySelectorAll(selector));
          const totalCount = btns.length;
          let clickedCount = 0;
          let idx = 0;
          const clickNext = () => {
            if (seq !== sceneSeqRef.current) return;
            if (idx >= btns.length) {
              // Optionally hit the Finish button to close the dialog cleanly.
              if (interaction.finishAfter) {
                const done = document.querySelector('[data-testid="enroll-done-btn"]');
                if (done) {
                  const r = done.getBoundingClientRect();
                  setCursorState({ visible: true, x: r.left + r.width / 2, y: r.top + r.height / 2, clicking: true, label: 'Finish' });
                  try { done.click(); } catch (_) { /* */ }
                }
              }
              // Confetti-style cascade of toasts summarising the side-effects.
              // Spaced so each one lands distinctly rather than stacking into a
              // single blob — viewers see the data fanning out across systems.
              if (interaction.celebrate !== false && clickedCount > 0) {
                const summary = interaction.celebrateSummary || [
                  { kind: 'success', text: `${clickedCount} program${clickedCount === 1 ? '' : 's'} enrolled` },
                  { kind: 'info',    text: '1 outcome event fired \u2192 grants dashboard' },
                  { kind: 'info',    text: '1 funder indicator updated \u2192 quarterly report draft' },
                ];
                summary.forEach((item, i) => {
                  const ct = setTimeout(() => {
                    if (seq !== sceneSeqRef.current) return;
                    const fn = item.kind === 'success' ? toast.success
                             : item.kind === 'warning' ? toast.warning
                             : item.kind === 'error'   ? toast.error
                                                       : toast.info;
                    const opts = { duration: 4800, position: 'top-right' };
                    if (item.link?.route) {
                      opts.action = {
                        label: item.link.label || 'View \u2192',
                        onClick: () => { try { window.open(item.link.route, '_blank', 'noopener,noreferrer'); } catch (_) { /* */ } },
                      };
                    }
                    try { fn(item.text, opts); } catch (_) { /* */ }
                  }, 800 + i * 700);
                  interactionTimersRef.current.push(ct);
                });
              }
              return;
            }
            const btn = btns[idx];
            if (!btn || btn.disabled) { idx += 1; const t = setTimeout(clickNext, step); interactionTimersRef.current.push(t); return; }
            const r = btn.getBoundingClientRect();
            setCursorState({ visible: true, x: r.left + r.width / 2, y: r.top + r.height / 2, clicking: true, label: `Enroll \u2192 ${btn.innerText?.trim()?.slice(0, 24) || ''}` });
            try { btn.click(); clickedCount += 1; } catch (_) { /* */ }
            idx += 1;
            const drop = setTimeout(() => setCursorState(prev => ({ ...prev, clicking: false })), 350);
            interactionTimersRef.current.push(drop);
            const t = setTimeout(clickNext, step);
            interactionTimersRef.current.push(t);
          };
          clickNext();
        }, 600);
        interactionTimersRef.current.push(start);
      } else if (action === 'clickFirstMatch') {
        // clickFirstMatch is now handled transparently at the target-resolution
        // step above (via interaction.selectors[]). This branch retained as a
        // legacy no-op alias — declaring `action: 'clickFirstMatch'` alongside
        // `selectors: [...]` produces the same DOM click + cursor glide the
        // standard 'click' path renders.
      } else if (action === 'celebrate') {
        // Standalone toast cascade — fire at any moment in any scene to make
        // an invisible backend side-effect visible to viewers. Each `item`
        // lands ~700 ms after the previous so they read sequentially rather
        // than stacking into a single blob.
        //
        // Optional `item.link = { label, route }` renders a sonner action
        // button on the toast that opens `route` in a new tab — the magical
        // "open a second window mid-pitch" demo move where viewers click
        // from the celebration straight into the role's actual dashboard
        // and see the side-effect for real.
        const items = interaction.summary || [];
        const spacing = Math.max(350, interaction.spacing || 750);
        const duration = Math.max(1500, interaction.duration || 4800);
        items.forEach((item, i) => {
          const ct = setTimeout(() => {
            if (seq !== sceneSeqRef.current) return;
            const fn = item.kind === 'success' ? toast.success
                     : item.kind === 'warning' ? toast.warning
                     : item.kind === 'error'   ? toast.error
                                               : toast.info;
            const opts = { duration, position: 'top-right' };
            if (item.link?.route) {
              opts.action = {
                label: item.link.label || 'View \u2192',
                onClick: () => {
                  try { window.open(item.link.route, '_blank', 'noopener,noreferrer'); } catch (_) { /* */ }
                },
              };
            }
            try {
              fn(item.text, opts);
            } catch (_) { /* */ }
          }, i * spacing);
          interactionTimersRef.current.push(ct);
        });
      }
    };

    list.forEach(interaction => {
      const delay = Math.max(0, interaction.at || 0);
      const timer = setTimeout(() => performInteraction(interaction), delay);
      interactionTimersRef.current.push(timer);
    });
  }, [clearInteractionTimers]);

  // ── Scene transition: persona login + route navigation ───────────────
  // Beats primer
  //   A scene may declare `beats: [{ say, spotlight, spotlightLabel, interactions, autoAdvanceMs }]`.
  //   Each beat is one narrated sentence tightly bound to its own spotlight.
  //   The engine plays beats sequentially: on voice.onEnd of beat N, advance
  //   to beat N+1; on the last beat's onEnd, advance to the next scene.
  //   Scenes without `beats` fall through to the legacy path (one big TTS
  //   blob covering title + narrative + highlight, one spotlight).
  const playBeat = useCallback((scene, beatIdx, seq) => {
    if (seq !== sceneSeqRef.current) return;
    const beats = Array.isArray(scene?.beats) ? scene.beats : null;
    if (!beats || !beats[beatIdx]) return;
    const beat = beats[beatIdx];
    setBeatIndex(beatIdx);

    // Cursor + timer reset for each beat so previous-beat interactions
    // don't linger onto the next spotlight.
    clearInteractionTimers();
    setCursorState(prev => ({ ...prev, visible: false, clicking: false, label: '' }));

    // Prefix scene title on beat 0 so the narrator opens with the scene
    // headline before diving into the first sentence — matches the cadence
    // of the legacy scenes ("Step 1: name, category, and portal presence.
    // Sarah names it Career Launch Pathway…").
    const say = beatIdx === 0 && scene.title ? `${scene.title}. ${beat.say}` : beat.say;

    // Estimated beat duration hint used ONLY by the muted-fallback timer.
    // ~180 ms per word plus 800 ms buffer, capped so we never let a beat
    // silently stall the tour.
    const wordCount = (beat.say || '').split(/\s+/).filter(Boolean).length;
    const hintMs = beat.autoAdvanceMs || Math.min(15000, Math.max(3200, wordCount * 190 + 800));

    // Compute a minimum-hold-time so this beat cannot advance until every
    // scheduled interaction has had time to fire its full cursor glide + click
    // + settle. Without this, short beats like "Next." finish narration in
    // ~700 ms and auto-advance BEFORE the click interaction (at:900 + 1150 ms
    // glide = ~2050 ms) fires — clearInteractionTimers on scene change then
    // cancels the pending click, so the wizard never advances.
    //
    // Formula: latest `at` in the beat + 1600 ms (~1150 ms glide + 400 ms
    // ripple + 50 ms slack). If there are no interactions, minHold is 0.
    const beatStartedAt = Date.now();
    const maxAt = (beat.interactions || []).reduce((m, i) => Math.max(m, i.at || 0), 0);
    const minHoldMs = (beat.interactions && beat.interactions.length > 0) ? maxAt + 1600 : 0;

    const advanceBeat = ({ skipped } = {}) => {
      if (seq !== sceneSeqRef.current) return;
      if (!isPlayingRef.current) return;
      if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
      if (fallbackAdvanceTimerRef.current) { clearTimeout(fallbackAdvanceTimerRef.current); fallbackAdvanceTimerRef.current = null; }
      // Tiny grace before the next beat so cursor + spotlight animations
      // don't step on the tail of the previous audio, but small enough that
      // the narrator sounds like they're pausing between sentences — not
      // waiting for the network. Skipped/muted beats get a shorter gap so
      // the tour still keeps moving.
      // (Bumped from 180 → 380 to give viewers a heartbeat of read-time
      // after the audio finishes before the next beat lights up.)
      const naturalGap = skipped ? 140 : 380;
      const elapsed = Date.now() - beatStartedAt;
      // Never advance before the last interaction had time to complete.
      const gap = Math.max(naturalGap, minHoldMs - elapsed + 120);
      advanceTimerRef.current = setTimeout(() => {
        if (seq !== sceneSeqRef.current) return;
        if (!isPlayingRef.current) return;
        if (beatIdx + 1 < beats.length) {
          playBeat(scene, beatIdx + 1, seq);
        } else if (sceneIndexRef.current + 1 >= totalScenesRef.current) {
          endTourRef.current?.();
        } else {
          setSceneIndex(i => Math.min(totalScenesRef.current - 1, i + 1));
        }
      }, gap);
    };

    // Muted-fallback: if voice never actually starts (autoplay blocked,
    // network error, or user muted), still advance the beat after hintMs.
    // Guarded by voiceSpeakingRef so a slow-loading beat whose audio starts
    // AFTER we scheduled this timer still gets to finish naturally — if
    // audio is playing when the timer fires, we simply requeue instead of
    // cutting it off mid-sentence.
    if (fallbackAdvanceTimerRef.current) clearTimeout(fallbackAdvanceTimerRef.current);
    const scheduleFallback = (delay) => {
      fallbackAdvanceTimerRef.current = setTimeout(() => {
        if (seq !== sceneSeqRef.current) return;
        if (!isPlayingRef.current) return;
        // Audio is currently playing — don't cut it off. Re-check in 2s.
        if (voiceSpeakingRef.current) {
          scheduleFallback(2000);
          return;
        }
        advanceBeat({ skipped: true });
      }, delay);
    };
    scheduleFallback(hintMs + 3500);

    voice.speak(say, {
      onEnd: ({ skipped }) => {
        if (seq !== sceneSeqRef.current) return;
        if (!isPlayingRef.current) return;
        if (skipped) return; // fallback timer above will handle it
        advanceBeat({ skipped: false });
      },
    });

    // Warm the NEXT beat's TTS blob (and the one after, when the immediate
    // next is very short) so back-to-back sentences don't stall on the
    // network round-trip. The prefetch cache in useTourVoice de-duplicates
    // requests, so overlapping prefetches are safe.
    if (voice.prefetch) {
      const nextBeat = beats[beatIdx + 1];
      if (nextBeat?.say) {
        try { voice.prefetch(nextBeat.say); } catch (_) { /* */ }
      }
      const afterNext = beats[beatIdx + 2];
      if (afterNext?.say && (nextBeat?.say?.length || 0) < 60) {
        try { voice.prefetch(afterNext.say); } catch (_) { /* */ }
      }
      // Last beat of the scene → warm the FIRST beat of the next scene so
      // the scene boundary feels as smooth as a beat boundary.
      if (beatIdx === beats.length - 1) {
        const nextScene = tour?.scenes?.[sceneIndex + 1];
        const firstNextBeat = nextScene?.beats?.[0];
        if (firstNextBeat?.say) {
          const preface = nextScene.title ? `${nextScene.title}. ${firstNextBeat.say}` : firstNextBeat.say;
          try { voice.prefetch(preface); } catch (_) { /* */ }
        }
      }
    }

    // Run beat-scoped interactions (their `at` offsets are relative to
    // this beat's start, not the scene).
    runInteractions({ interactions: beat.interactions || [] }, seq);
  }, [voice, clearInteractionTimers, tour, sceneIndex, runInteractions]);

  useEffect(() => {
    if (!tour || !currentScene) return;
    const seq = ++sceneSeqRef.current;
    setTransitioning(true);
    setBeatIndex(0);
    (async () => {
      await loginAsPersona(currentScene.persona?.role);
      if (seq !== sceneSeqRef.current) return; // stale — newer scene already running
      // Let AppContext's setCurrentUser flush before navigating into protected routes
      await flushReactState();
      if (seq !== sceneSeqRef.current) return;
      // Narrator-only scenes (no real product screen) don't navigate — the
      // global NarratorBackdrop overlay covers whatever route is loaded.
      const isNarratorScene = currentScene.persona?.role === 'Narrator' || currentScene.persona?.role === 'System';
      if (currentScene.route && !isNarratorScene) {
        try { navigate(currentScene.route); } catch (_) { /* ignore */ }
      }
      setTransitioning(false);

      // ── Beats mode ──
      // Sentence-level narration + sentence-level spotlight. Each beat is
      // its own TTS request (small payloads = fewer OpenAI timeouts, no more
      // "silent scene" incidents) and its own tightly-focused UI target.
      if (Array.isArray(currentScene.beats) && currentScene.beats.length > 0) {
        playBeat(currentScene, 0, seq);
        if (runId) {
          safeFetch(`${apiBase}/tours/runs/${runId}/log`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: 'scene.enter',
              sceneIndex,
              sceneTitle: currentScene.title,
              persona: currentScene.persona,
              surface: currentScene.route || '',
              beatsMode: true,
              beatCount: currentScene.beats.length,
            }),
          });
        }
        return;
      }

      // ── Legacy mode (single blob narration) ──
      // Build the spoken script. Include the highlight text (the green/amber
      // strip at the bottom of the panel) so the narrator covers every visible
      // line, not just the headline + paragraphs.
      const script = [
        currentScene.title,
        ...(currentScene.narrative || []),
        currentScene.highlight?.text
          ? `${currentScene.highlight.kind === 'tip' ? 'Tip' :
              currentScene.highlight.kind === 'tech' ? 'Behind the scenes' :
              currentScene.highlight.kind === 'role' ? 'Access control' :
              currentScene.highlight.kind === 'magic' ? 'Live magic' :
              currentScene.highlight.kind === 'outcome' ? 'Outcome' : ''}: ${currentScene.highlight.text}`
          : null,
      ].filter(Boolean).join('. ');
      // When narration ends, schedule auto-advance (or auto-close on the last
      // scene). When narration is skipped (muted or unsupported), the
      // standalone fallback timer below handles advancement.
      voice.speak(script, {
        onEnd: ({ skipped }) => {
          if (skipped) return; // fallback safety-net timer handles it
          if (seq !== sceneSeqRef.current) return; // stale (scene already changed)
          if (!isPlayingRef.current) return;
          // We have an authoritative narration-end signal — cancel the
          // safety-net fallback so it can't double-fire after we advance.
          if (fallbackAdvanceTimerRef.current) {
            clearTimeout(fallbackAdvanceTimerRef.current);
            fallbackAdvanceTimerRef.current = null;
          }
          if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
          advanceTimerRef.current = setTimeout(() => {
            if (seq !== sceneSeqRef.current) return;
            if (!isPlayingRef.current) return;
            // Last scene? Auto-close the tour for a clean ending.
            if (sceneIndexRef.current + 1 >= totalScenesRef.current) {
              endTourRef.current?.();
            } else {
              setSceneIndex(i => Math.min(totalScenesRef.current - 1, i + 1));
            }
          }, 1100);
        },
      });
      // Schedule the per-scene cursor interactions.
      runInteractions(currentScene, seq);
      // Log scene.enter (best-effort)
      if (runId) {
        safeFetch(`${apiBase}/tours/runs/${runId}/log`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'scene.enter',
            sceneIndex,
            sceneTitle: currentScene.title,
            persona: currentScene.persona,
            surface: currentScene.route || '',
          }),
        });
      }
    })();
  // The persona/route switch should re-run only when the tour or scene
  // changes. We intentionally read the latest currentScene/runId/voice
  // inside the effect to avoid spurious re-runs.
  }, [tour, sceneIndex]);

  // Reset elapsed on scene change + clear any in-flight cursor interactions
  // and pending auto-advance timers so the new scene starts cleanly.
  useEffect(() => {
    setElapsedMs(0);
    clearInteractionTimers();
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    if (fallbackAdvanceTimerRef.current) { clearTimeout(fallbackAdvanceTimerRef.current); fallbackAdvanceTimerRef.current = null; }
    setCursorState(prev => ({ ...prev, visible: false, clicking: false }));
  }, [sceneIndex, clearInteractionTimers]);

  // Hold a fresh reference to endTour so the voice onEnd callback (captured at
  // scene-start time) can auto-close the tour on the last scene without us
  // needing to wire endTour into its dependency array.
  const endTourRef = useRef(null);

  // Auto-advance:
  //   • If voice is muted or unsupported → fall back to a simple per-scene
  //     timer (autoAdvanceMs).
  //   • If voice is speaking → the `voice.speak()` onEnd callback in the scene
  //     transition effect handles advancement.
  // We still tick the progress bar (elapsedMs / sceneMs) every TICK_MS so the
  // UI shows visible motion regardless of which path is driving the timing.
  useEffect(() => {
    clearInterval(tickRef.current);
    if (!tour || !isPlaying || transitioning) return undefined;
    tickRef.current = setInterval(() => {
      setElapsedMs(prev => {
        const next = prev + TICK_MS;
        // Cap the progress bar at 100% without firing the muted fallback path.
        return next >= sceneMs ? sceneMs : next;
      });
    }, TICK_MS);
    return () => clearInterval(tickRef.current);
  }, [tour, isPlaying, transitioning, sceneMs]);

  // Always-on fallback auto-advance — the safety net.
  //   • Voice path (voice.onEnd) is the *primary* advance signal: when the
  //     narrator audio actually finishes, it cancels this fallback and
  //     schedules a faster 1.1 s grace.
  //   • This timer ONLY fires if voice fails / is muted / autoplay was blocked
  //     or some other error stops the narrator from finishing. The extra
  //     4 s buffer keeps it well outside normal narration so the two paths
  //     don't race.
  //   • Scenes in **beats mode** manage their own per-beat fallback inside
  //     `playBeat`, so we skip this scene-level timer for them (otherwise it
  //     would fire mid-beat and jump straight to the next scene).
  useEffect(() => {
    if (!tour || !isPlaying || transitioning) return undefined;
    if (Array.isArray(currentScene?.beats) && currentScene.beats.length > 0) return undefined;
    if (fallbackAdvanceTimerRef.current) clearTimeout(fallbackAdvanceTimerRef.current);
    const seq = sceneSeqRef.current;
    fallbackAdvanceTimerRef.current = setTimeout(() => {
      if (seq !== sceneSeqRef.current) return;
      if (!isPlayingRef.current) return;
      if (sceneIndexRef.current + 1 >= totalScenesRef.current) {
        endTourRef.current?.();
      } else {
        setSceneIndex(i => Math.min(totalScenesRef.current - 1, i + 1));
      }
    }, sceneMs + 4000);
    return () => {
      if (fallbackAdvanceTimerRef.current) {
        clearTimeout(fallbackAdvanceTimerRef.current);
        fallbackAdvanceTimerRef.current = null;
      }
    };
  }, [tour, isPlaying, transitioning, sceneMs, sceneIndex, currentScene]);

  // Keep the mirror refs current so onEnd callbacks read fresh values
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { sceneIndexRef.current = sceneIndex; }, [sceneIndex]);
  useEffect(() => { totalScenesRef.current = totalScenes; }, [totalScenes]);
  useEffect(() => { endTourRef.current = endTour; }, [endTour]);

  // Mirror voice.speaking into a ref so async callbacks (setTimeout closures
  // for the fallback advance timer) can read the LATEST value, not the value
  // captured at the moment the timer was scheduled. Without this, a fallback
  // scheduled during a slow TTS fetch could fire ~10s later, see the stale
  // "voice not yet speaking" closure value, and cut off a narration that
  // has since started playing normally.
  const voiceSpeakingRef = useRef(false);
  useEffect(() => { voiceSpeakingRef.current = voice.speaking; }, [voice.speaking]);

  // The fallback safety-net timer is set to `sceneMs + 4000` — but real
  // OpenAI narrations are often longer than the scene's hint duration. Once
  // the audio actually starts playing (voice.speaking flips to true) we know
  // the narrator is in control and voice.onEnd will signal the authoritative
  // advance. Kill the fallback the moment audio starts so it can never
  // pre-empt a still-playing narration.
  useEffect(() => {
    if (voice.speaking && fallbackAdvanceTimerRef.current) {
      clearTimeout(fallbackAdvanceTimerRef.current);
      fallbackAdvanceTimerRef.current = null;
    }
  }, [voice.speaking]);

  // Controls.
  // Manual prev/next preserves auto-play so the rest of the tour continues
  // hands-free after a viewer skips a scene. We only flip `isPlaying` to
  // false on explicit pause or end-of-tour.
  const advance  = useCallback(() => {
    voice.cancel();
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    if (fallbackAdvanceTimerRef.current) { clearTimeout(fallbackAdvanceTimerRef.current); fallbackAdvanceTimerRef.current = null; }
    setSceneIndex(i => Math.min(totalScenes - 1, i + 1));
  }, [totalScenes, voice]);
  const back     = useCallback(() => {
    voice.cancel();
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    if (fallbackAdvanceTimerRef.current) { clearTimeout(fallbackAdvanceTimerRef.current); fallbackAdvanceTimerRef.current = null; }
    setSceneIndex(i => Math.max(0, i - 1));
  }, [voice]);
  const togglePlay = useCallback(() => {
    setIsPlaying(p => {
      // Pausing cancels the narrator + any pending auto-advance.
      // Resuming will re-narrate the current scene (which re-schedules the
      // post-narration advance through voice.speak's onEnd callback).
      if (p) {
        voice.cancel();
        if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
      } else if (currentScene) {
        const script = [
          currentScene.title,
          ...(currentScene.narrative || []),
          currentScene.highlight?.text ? `${currentScene.highlight.kind || 'note'}: ${currentScene.highlight.text}` : null,
        ].filter(Boolean).join('. ');
        voice.speak(script, {
          onEnd: ({ skipped }) => {
            if (skipped) return;
            if (!isPlayingRef.current) return;
            if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
            advanceTimerRef.current = setTimeout(() => {
              if (!isPlayingRef.current) return;
              if (sceneIndexRef.current + 1 >= totalScenesRef.current) {
                endTourRef.current?.();
              } else {
                setSceneIndex(i => Math.min(totalScenesRef.current - 1, i + 1));
              }
            }, 1100);
          },
        });
      }
      return !p;
    });
  }, [voice, currentScene]);
  const restart  = useCallback(() => {
    voice.cancel();
    setSceneIndex(0);
    setIsPlaying(true);
  }, [voice]);

  // Fullscreen toggle (keyboard 'F' + button). Uses the standard Fullscreen
  // API on the document root so the entire app — real product screens + the
  // floating narrator panel + spotlight overlay — go immersive together.
  const toggleFullscreen = useCallback(() => {
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        document.documentElement.requestFullscreen?.();
      }
    } catch (_) { /* some browsers block without user gesture; ignore */ }
  }, []);

  // Track fullscreen state so the UI can show the right icon
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Keyboard cheat-sheet overlay
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const toggleShortcuts = useCallback(() => setShortcutsOpen(s => !s), []);

  // Keyboard shortcuts: → next · ← prev · Space play/pause · F fullscreen · ? cheat sheet.
  // Skip when the user is typing in an editable element so we don't hijack
  // form input on real product screens during a tour.
  useEffect(() => {
    if (!tour) return undefined;
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };
    const onKey = (e) => {
      if (isEditable(document.activeElement)) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); advance(); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); back(); }
      else if (e.key === ' ' || e.code === 'Space') { e.preventDefault(); togglePlay(); }
      else if (e.key === 'f' || e.key === 'F') { e.preventDefault(); toggleFullscreen(); }
      else if (e.key === '?') { e.preventDefault(); toggleShortcuts(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tour, advance, back, togglePlay, toggleFullscreen, toggleShortcuts]);

  // Active spotlight resolution.
  //   • In beats mode → the current beat's spotlight (fine-grained per sentence).
  //   • Otherwise → the scene-level spotlight (legacy).
  //   • Beats without a `spotlight` inherit the scene's spotlight so authors
  //     can leave beats spotlight-less when the frame doesn't need to move.
  const activeBeat = (Array.isArray(currentScene?.beats) && currentScene.beats[beatIndex]) || null;
  const _rawSpotlight = activeBeat
    ? (activeBeat.spotlight || currentScene?.spotlight || null)
    : (currentScene?.spotlight || null);
  // Interpolate `${ctx.xxx}` tokens against window.__tourCtx so spotlight
  // selectors can point at DOM created earlier in the tour (event ids,
  // community-member ids threaded via `district:tour-ctx`).
  const activeSpotlight = interpolateCtx(_rawSpotlight);
  const activeSpotlightLabel = activeBeat
    ? (activeBeat.spotlightLabel || currentScene?.spotlightLabel || null)
    : (currentScene?.spotlightLabel || null);

  const value = useMemo(() => ({
    tour, sceneIndex, currentScene, totalScenes, isPlaying, elapsedMs, sceneMs, transitioning,
    isActive: !!tour, location,
    startTour, endTour, advance, back, togglePlay, restart,
    voice,
    isFullscreen, toggleFullscreen,
    shortcutsOpen, toggleShortcuts,
    cursorState,
    tourStartedAt,
    beatIndex, activeBeat, activeSpotlight, activeSpotlightLabel,
  }), [tour, sceneIndex, currentScene, totalScenes, isPlaying, elapsedMs, sceneMs, transitioning, location, startTour, endTour, advance, back, togglePlay, restart, voice, isFullscreen, toggleFullscreen, shortcutsOpen, toggleShortcuts, cursorState, tourStartedAt, beatIndex, activeBeat, activeSpotlight, activeSpotlightLabel]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
}
